import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../config/supabase';
import { useApp } from '../contexts/AppContext';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../utils/errorLogging';
import { TABLES } from '../constants/supabaseConstants';

// Helper function for consistent cache merging
const mergeCacheItem = (existing, updates) => ({ ...existing, ...updates });

/**
 * Helper to call an RPC that returns an ID, then fetch and return the full object.
 * Use this when creating items that need immediate selection/navigation.
 *
 * @param {string} rpcName - Name of the RPC function
 * @param {object} params - Parameters to pass to the RPC
 * @param {string} tableName - Table to fetch the full object from
 * @param {function} logError - Error logging function
 * @returns {Promise<object>} The full created object
 */
const createAndReturn = async (rpcName, params, tableName, logError) => {
  const { data: id, error } = await supabase.rpc(rpcName, params);
  if (error) {
    logError({
      severity: ERROR_SEVERITY.ERROR,
      category: ERROR_CATEGORY.DATA,
      message: `Failed to call ${rpcName}`,
      error,
    });
    throw error;
  }

  // Fetch the full object
  const { data: fullObject, error: fetchError } = await supabase
    .from(tableName)
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) {
    logError({
      severity: ERROR_SEVERITY.WARNING,
      category: ERROR_CATEGORY.DATA,
      message: `${rpcName} succeeded but failed to fetch full object`,
      error: fetchError,
    });
    // Return ID as fallback
    return { id };
  }

  return fullObject;
};

const validateFloorData = floor => {
  const errors = {};

  // Required fields
  if (!floor.floor_number && floor.floor_number !== 0) {
    errors.floor_number = 'מספר קומה נדרש';
  }

  // Numeric validations
  if (floor.floor_height <= 0) {
    errors.floor_height = 'גובה קומה חייב להיות גדול מ-0';
  }

  if (floor.stairs_to_floor < 0) {
    errors.stairs_to_floor = 'מספר מדרגות חייב להיות 0 או חיובי';
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const useData = (tableName, options = {}) => {
  const session = useSession();
  const queryClient = useQueryClient();
  const { currentProject } = useApp();
  const { logError } = useErrorLogging();

  // Determine if query should be enabled
  const isEnabled = typeof options.enabled === 'boolean' ? options.enabled && !!session : !!session;

  // Use custom queryKey if provided, otherwise default to [tableName, filter]
  const effectiveQueryKey = options.queryKey || [tableName, options.filter || {}];

  // Query for fetching data
  const query = useQuery({
    queryKey: effectiveQueryKey,
    queryFn: async () => {
      let queryBuilder = supabase.from(tableName).select(options.select || '*');

      // Apply any additional filters
      if (options.filter) {
        Object.entries(options.filter).forEach(([key, value]) => {
          if (value !== undefined) {
            // If value is array, use .in(); if null, use .is(); otherwise use .eq()
            if (Array.isArray(value)) {
              queryBuilder = queryBuilder.in(key, value);
            } else if (value === null) {
              queryBuilder = queryBuilder.is(key, null);
            } else {
              queryBuilder = queryBuilder.eq(key, value);
            }
          }
        });
      }

      // Apply OR filter if provided (PostgREST syntax)
      if (options.orFilter) {
        queryBuilder = queryBuilder.or(options.orFilter);
      }

      // Return single record if requested
      if (options.single) {
        queryBuilder = queryBuilder.single();
      }

      const { data, error } = await queryBuilder;
      if (error) throw error;

      return data;
    },
    enabled: isEnabled,
    staleTime: options.staleTime,
  });

  // Subscribe to realtime changes
  useEffect(() => {
    if (!isEnabled) return;

    // Create channel for realtime subscription
    const channel = supabase
      .channel(`${tableName}_${options.select || '*'}_changes`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: tableName,
        },
        payload => {
          // Use payload for efficient cache updates
          const { eventType, new: newRecord, old: oldRecord } = payload;

          // Use the same query key as our initial query
          const queryKey = effectiveQueryKey;

          // Optimistically update cache based on event type
          if (eventType === 'INSERT') {
            // Check if the new record matches our filter criteria before adding to cache
            const matchesFilter =
              !options.filter ||
              Object.entries(options.filter).every(([key, value]) => {
                if (value === undefined) return true;
                if (Array.isArray(value)) {
                  return value.includes(newRecord[key]);
                }
                if (value === null) {
                  return newRecord[key] === null;
                }
                return newRecord[key] === value;
              });

            if (matchesFilter) {
              queryClient.setQueryData(queryKey, old => {
                if (!Array.isArray(old)) return old; // Skip non-array cache (e.g. .single() queries)
                return [...old, newRecord];
              });
            }
          } else if (eventType === 'DELETE') {
            queryClient.setQueryData(queryKey, old => {
              if (!Array.isArray(old)) return old; // Skip non-array cache (e.g. .single() queries)
              return old.filter(item => item.id !== oldRecord.id);
            });
          } else if (eventType === 'UPDATE') {
            queryClient.setQueryData(queryKey, old => {
              if (!Array.isArray(old)) return old; // Skip non-array cache (e.g. .single() queries)
              return old.map(item => {
                // If this is the updated item, merge while preserving joined data
                if (item.id === newRecord.id) {
                  // Keep all joined data (fields with nested objects)
                  const joinedData = {};
                  Object.entries(item).forEach(([key, value]) => {
                    if (value && typeof value === 'object' && !Array.isArray(value)) {
                      joinedData[key] = value;
                    }
                  });

                  // Merge new data with existing joined data
                  return { ...mergeCacheItem(item, newRecord), ...joinedData };
                }
                return item;
              });
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tableName, queryClient, isEnabled, options.select, effectiveQueryKey]);

  // Mutation for inserting data
  const insert = useMutation({
    mutationFn: async data => {
      const { error, data: result } = await supabase.from(tableName).insert(data).select();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([tableName]);
    },
  });

  // Mutation for updating data
  const update = useMutation({
    mutationFn: async ({ id, data }) => {
      const { error, data: result } = await supabase
        .from(tableName)
        .update(data)
        .eq('id', id)
        .select('*');

      if (error) throw error;
      return result;
    },
    onSuccess: (result, variables) => {
      queryClient.setQueryData([tableName], old =>
        old?.map(item => (item.id === variables.id ? mergeCacheItem(item, result[0]) : item)),
      );
      queryClient.invalidateQueries([tableName]);
    },
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA,
        message: 'Update failed',
      });
    },
  });

  // Mutation for deleting data
  const remove = useMutation({
    mutationFn: async id => {
      const { error } = await supabase.from(tableName).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([tableName]);
    },
  });

  // Complex operations using RPC
  const manageSpaces = useMutation({
    mutationFn: async spaces => {
      const { data, error } = await supabase.rpc('manage_project_spaces', {
        p_project_id: currentProject.id,
        p_spaces: spaces,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project_spaces', 'project_floors']);
    },
  });

  const manageFloorsMutation = useMutation({
    mutationFn: async floors => {
      try {
        // Validate floor data
        const validationErrors = floors.map(validateFloorData).filter(Boolean);
        if (validationErrors.length > 0) {
          throw new Error('Floor validation failed', { cause: validationErrors[0] });
        }

        // Call RPC
        const result = await supabase.rpc('manage_project_floors', {
          p_project_id: currentProject.id,
          p_floors: floors,
        });

        if (result.error) {
          // Handle specific error types
          if (result.error.code === '23505') {
            // Unique violation
            throw new Error('Floor validation failed', {
              cause: { floor_number: 'קומה עם מספר זה כבר קיימת' },
            });
          }
          throw result.error;
        }

        return result.data;
      } catch (error) {
        logError({
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA,
          message: 'Failed to manage floors',
          error,
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project_floors', 'project_spaces']);
    },
  });

  const manageTasks = useMutation({
    mutationFn: async tasks => {
      const { data, error } = await supabase.rpc('manage_project_tasks', {
        p_project_id: currentProject.id,
        p_tasks: tasks,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project_tasks', 'project_task_dependencies']);
    },
  });

  const manageBudget = useMutation({
    mutationFn: async expenses => {
      const { data, error } = await supabase.rpc('manage_project_budget', {
        p_project_id: currentProject.id,
        p_expenses: expenses,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project_expenses', 'project_info']);
    },
  });

  const manageProject = useMutation({
    mutationFn: async projectData => {
      return createAndReturn('manage_project', projectData, TABLES.PROJECT_INFO, logError);
    },
    onSuccess: newProject => {
      // Add new project to cache immediately
      queryClient.setQueryData([TABLES.PROJECT_INFO, {}], old =>
        old ? [...old, newProject] : [newProject],
      );
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries([TABLES.PROJECT_INFO]);
      queryClient.invalidateQueries([TABLES.JOIN_PROJECT_USERS]);
      queryClient.invalidateQueries([TABLES.PROJECT_FLOORS]);
    },
  });

  const manageBoqItems = useMutation({
    mutationFn: async items => {
      const { data, error } = await supabase.rpc('manage_boq_items', {
        p_project_id: currentProject.id,
        p_items: items,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TABLES.PROJECT_ITEMS]);
    },
  });

  // Mutation for soft deleting data (generic - use softDeleteSpace for spaces)
  const softDelete = useMutation({
    mutationFn: async id => {
      const { error, data: result } = await supabase
        .from(tableName)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .select();
      if (error) throw error;
      return result;
    },
    onSuccess: (result, deletedId) => {
      // Optimistically remove from cache immediately
      queryClient.setQueryData(effectiveQueryKey, old =>
        old?.filter(item => item.id !== deletedId),
      );
      // Also invalidate to ensure consistency across all queries for this table
      queryClient.invalidateQueries({ queryKey: [tableName] });
    },
  });

  // Mutation for cascade soft-delete of a space (deletes space + items + boq + windows)
  const softDeleteSpaceMutation = useMutation({
    mutationFn: async spaceId => {
      const { data, error } = await supabase.rpc('soft_delete_space', {
        p_space_id: spaceId,
      });
      if (error) throw error;
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete space');
      }
      return data;
    },
    onSuccess: (result, deletedSpaceId) => {
      // Optimistically remove space from cache
      queryClient.setQueryData(effectiveQueryKey, old =>
        old?.filter(item => item.id !== deletedSpaceId),
      );
      // Invalidate all affected tables
      queryClient.invalidateQueries({ queryKey: [TABLES.PROJECT_SPACES] });
      queryClient.invalidateQueries({ queryKey: [TABLES.PROJECT_ITEMS] });
      queryClient.invalidateQueries({ queryKey: [TABLES.PROJECT_WINDOWS] });
    },
  });

  // Mutation for managing project items
  const manageProjectItems = useMutation({
    mutationFn: async ({ p_project_id, p_items_data, p_cost_type_override }) => {
      const { data, error } = await supabase.rpc('manage_project_items', {
        p_project_id,
        p_items_data,
        p_cost_type_override,
      });
      if (error) throw error;
      if (data && data.success === false) {
        throw new Error(data.error || 'RPC returned failure');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TABLES.PROJECT_ITEMS]);
    },
  });

  // Mutation for handling document uploads
  const handleDocument = useMutation({
    mutationFn: async ({ projectId, recordId, recordType, file }) => {
      // Convert file to ArrayBuffer for sending to RPC
      const arrayBuffer = await file.arrayBuffer();
      const fileBytes = new Uint8Array(arrayBuffer);

      const { data, error } = await supabase.rpc('handle_document_upload', {
        p_project_id: projectId,
        p_record_id: recordId,
        p_record_type: recordType,
        p_file: fileBytes,
        p_file_name: file.name,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TABLES.PROJECT_PAYMENTS]);
    },
  });

  // Mutation for importing BOQ templates
  const importBoqTemplates = useMutation({
    mutationFn: async categoryIds => {
      const { data, error } = await supabase.rpc('import_boq_templates', {
        p_project_id: currentProject.id,
        p_category_ids: categoryIds,
      });
      if (error) {
        logError({
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA,
          message: 'Failed to import BOQ templates',
          error,
          metadata: { projectId: currentProject?.id, categoryIds },
        });
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TABLES.PROJECT_BOQ_TEMPLATE_STATUS]);
    },
  });

  // Mutation for managing project vendors
  const manageProjectVendor = useMutation({
    mutationFn: async ({ p_project_item_id, p_vendor_data, p_master_vendor_id }) => {
      try {
        const { data, error } = await supabase.rpc('manage_project_vendor', {
          p_project_item_id,
          p_vendor_data,
          p_master_vendor_id,
        });

        if (error) {
          logError({
            severity: ERROR_SEVERITY.ERROR,
            category: ERROR_CATEGORY.DATA,
            message: 'Failed to manage vendor',
            error,
          });
          throw error;
        }
        return data;
      } catch (error) {
        logError({
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA,
          message: 'Failed to manage vendor',
          error,
        });
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate all affected tables
      queryClient.invalidateQueries([TABLES.MASTER_VENDORS]);
      queryClient.invalidateQueries([TABLES.PROJECT_VENDORS]);
      queryClient.invalidateQueries([TABLES.PROJECT_ITEMS]);
    },
  });

  // Mutation for updating master vendor details
  // eslint-disable-next-line no-unused-vars
  const updateMasterVendor = useMutation({
    mutationFn: async ({ p_master_vendor_id, p_vendor_data }) => {
      const { data, error } = await supabase.rpc('update_master_vendor', {
        p_master_vendor_id,
        p_vendor_data,
      });

      if (error) {
        logError({
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA,
          message: 'Failed to update vendor',
          error,
        });
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TABLES.MASTER_VENDORS]);
      queryClient.invalidateQueries([TABLES.PROJECT_VENDORS]);
      queryClient.invalidateQueries([TABLES.PROJECT_ITEMS]);
    },
  });

  // Mutation for deleting project items
  const deleteProjectItem = useMutation({
    mutationFn: async itemId => {
      const { data, error } = await supabase.rpc('delete_project_item', {
        p_item_id: itemId,
      });
      if (error) {
        logError({
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA,
          message: 'Failed to delete project item',
          error,
          metadata: { itemId },
        });
        throw error;
      }
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete project item');
      }
      return data;
    },
    onSuccess: () => {
      // Invalidate all affected tables
      queryClient.invalidateQueries([TABLES.PROJECT_ITEMS, TABLES.PROJECT_VENDORS]);
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    insert: insert.mutateAsync,
    update: update.mutateAsync,
    remove: remove.mutateAsync,
    softDelete: softDelete.mutateAsync,
    refetch: query.refetch,
    // Complex operations
    manageSpaces: manageSpaces.mutateAsync,
    manageFloors: manageFloorsMutation.mutateAsync,
    manageTasks: manageTasks.mutateAsync,
    manageBudget: manageBudget.mutateAsync,
    manageProject: manageProject.mutateAsync,
    manageBoqItems: manageBoqItems.mutateAsync,
    manageProjectItems: manageProjectItems.mutateAsync,
    handleDocument: handleDocument.mutateAsync,
    manageProjectVendor: manageProjectVendor.mutateAsync,
    updateMasterVendor: updateMasterVendor.mutateAsync,
    importBoqTemplates: importBoqTemplates.mutateAsync,
    deleteProjectItem: deleteProjectItem.mutateAsync,
    softDeleteSpace: softDeleteSpaceMutation.mutateAsync,
  };
};
